import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { clsx } from '../../ds/clsx';
import { TextLink } from '../../ds/components';
import styles from './SignatureCanvas.module.css';

export interface SignatureCanvasProps {
  /** Accessible name of the pad („Unterschrift"). */
  label: string;
  /** Called with the PNG data URL after every stroke, with null after „Löschen". */
  onChange: (dataUrl: string | null) => void;
  /** Note under the pad („Regensburg, 04.09.2026 · mit Finger oder Maus unterschreiben"). */
  hint?: string | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

type Point = { x: number; y: number };

const LINE_WIDTH = 2.2;
/** Fixed export size (CSS pixels of the design pad), keeps the PNG well below the API limits. */
const EXPORT_WIDTH = 760;
const EXPORT_HEIGHT = 150;

function paint(context: CanvasRenderingContext2D, strokes: Point[][], scale: number, color: string): void {
  context.setTransform(scale, 0, 0, scale, 0, 0);
  context.lineWidth = LINE_WIDTH;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.strokeStyle = color;
  for (const stroke of strokes) {
    const [first] = stroke;
    if (!first) continue;
    context.beginPath();
    context.moveTo(first.x, first.y);
    if (stroke.length === 1) context.lineTo(first.x + 0.1, first.y + 0.1);
    for (const point of stroke.slice(1)) context.lineTo(point.x, point.y);
    context.stroke();
  }
}

/**
 * Signature pad of Selbstauskunft 05 / Reservierung 05: a `<canvas>` drawn with pointer events (finger,
 * pen, mouse), the baseline, „Löschen" at the top right, exported as PNG data URL (no library, PLAN 8.2).
 */
export function SignatureCanvas({
  label,
  onChange,
  hint,
  error,
  disabled,
  id,
  className,
}: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  const inkColor = useCallback((): string => {
    const canvas = canvasRef.current;
    if (!canvas) return '#0c1727';
    return getComputedStyle(canvas).getPropertyValue('--navy-900').trim() || '#0c1727';
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
    }
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    paint(context, strokes.current, ratio, inkColor());
  }, [inkColor]);

  useEffect(() => {
    redraw();
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => redraw());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [redraw]);

  function exportPng(): string {
    const canvas = canvasRef.current;
    const width = canvas?.clientWidth || EXPORT_WIDTH;
    const height = canvas?.clientHeight || EXPORT_HEIGHT;
    const scale = EXPORT_WIDTH / width;
    const offscreen = document.createElement('canvas');
    offscreen.width = EXPORT_WIDTH;
    offscreen.height = Math.round(height * scale);
    const context = offscreen.getContext('2d');
    if (!context) return '';
    paint(context, strokes.current, scale, inkColor());
    return offscreen.toDataURL('image/png');
  }

  function pointOf(event: ReactPointerEvent<HTMLCanvasElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function onPointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (disabled || event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    strokes.current.push([pointOf(event)]);
    redraw();
  }

  function onPointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    event.preventDefault();
    const current = strokes.current[strokes.current.length - 1];
    if (!current) return;
    current.push(pointOf(event));
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    const ratio = window.devicePixelRatio || 1;
    paint(context, [current.slice(-2)], ratio, inkColor());
  }

  function endStroke(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    drawing.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setEmpty(false);
    onChange(exportPng());
  }

  function clear() {
    strokes.current = [];
    redraw();
    setEmpty(true);
    onChange(null);
  }

  return (
    <div className={clsx(styles.wrapper, className)}>
      <div className={clsx(styles.pad, error && styles.invalid, disabled && styles.disabled)}>
        <canvas
          ref={canvasRef}
          id={id}
          role="img"
          aria-label={empty ? `${label} — noch nicht unterschrieben` : label}
          aria-invalid={error ? true : undefined}
          className={styles.canvas}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onLostPointerCapture={endStroke}
        />
        <span className={styles.baseline} aria-hidden="true" />
        {!empty && !disabled ? (
          <span className={styles.clear}>
            <TextLink as="button" size="small" onClick={clear}>
              Löschen
            </TextLink>
          </span>
        ) : null}
      </div>
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className={styles.hint}>{hint}</div>
      ) : null}
    </div>
  );
}
